import React, { Component } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import API from '../../../../utils/API';

class BoldSignEditorModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            error: null,
            embedUrl: this.props.editUrl || null,
            templateId: null,
            templateSaved: false
        };
        this.iframeRef = React.createRef();
    }

    componentDidMount() {
        // Listen for messages from BoldSign iframe
        window.addEventListener('message', this.handleMessage);
        // Create the embedded template when modal opens
        if(!this.props.editUrl) {
            this.createEmbeddedTemplate();
        } else {
            this.setState({ loading: false });
        }
    }

    componentWillUnmount() {
        window.removeEventListener('message', this.handleMessage);
    }

    createEmbeddedTemplate = async () => {
        const { orgId, eventId, pdfUrl, documentConfig } = this.props;
        
        try {
            this.setState({ loading: true, error: null });
            
            const response = await API().post(
                `Organizations/${orgId}/events/${eventId}/createBoldSignTemplate`,
                {
                    title: documentConfig?.title || 'Event Document Template',
                    description: documentConfig?.description || 'Template for event document signing',
                    documentTitle: documentConfig?.title || 'Document for Signature',
                    pdfUrl: pdfUrl
                }
            );

            if (response.data && response.data.embedUrl) {
                this.setState({
                    embedUrl: response.data.embedUrl,
                    templateId: response.data.templateId,
                    loading: false
                });
                
            } else {
                throw new Error('No embed URL returned from server');
            }
        } catch (error) {
            console.error('Error creating template:', error);
            this.setState({
                error: error.response?.data?.error || error.message || 'Failed to create template',
                loading: false
            });
        }
    };

    handleMessage = (event) => {
        // Accept messages from boldsign.com domains
        if (!event.origin || (!event.origin.includes('boldsign.com'))) {
            return;
        }
        
        const data = event.data;
        
        // Handle string messages (some APIs send JSON strings)
        let parsedData = data;
        if (typeof data === 'string') {
            try {
                parsedData = JSON.parse(data);
            } catch (e) {
                console.log('Non-JSON string message:', data);
                return;
            }
        }
                
        // BoldSign sends various events - handle the key ones
        // When user clicks "Save" or "Send" in the embedded editor
        
        // Check for success/completion indicators
        if (parsedData) {
            // Look for any indication the template was saved
            const isSaved = parsedData.status === 'onCreateSuccess'
            
            if (isSaved) {
                this.setState({ templateSaved: true });
                
                // Save to backend via parent component
                this.props.onSave({
                    boldSignTemplateId: this.state.templateId
                });
                
            }
            
        }
    };

    handleSave = () => {
        if(this.props.editUrl) {
            this.iframeRef.current.contentWindow.postMessage("onSaveClick", "*");
        } else {
            this.iframeRef.current.contentWindow.postMessage("onCreateClick", "*");
        }

    }

    render() {
        const { isOpen, toggle } = this.props;
        const { loading, error, embedUrl, templateSaved } = this.state;

        return (
            <Modal 
                isOpen={isOpen} 
                toggle={toggle} 
                className="document-sign-modal fullscreen"
                style={{ 
                    maxWidth: '100vw',
                    margin: 0,
                    padding: 0
                }}
                contentClassName="document-sign-modal-content"
                backdropClassName="document-sign-backdrop"
            >
				<div className='modalHeader ph-20' style={{ borderBottom: '1px solid var(--background)' }}>
					<div className='headerContainer' style={{ maxWidth: 'unset', minHeight: 60, height: 60 }}>
						<div className='flex aic'>
							<div>
								<h2 className=''>Document Editor</h2>
								<div className='flex aic '>
									<h3>{this.props.editUrl ? 'Editing Document Template' : 'Creating Document Template'}</h3>
								</div>
							</div>
						</div>
						<div className='flex aic'>
							<button className="neu mr-25 " onClick={toggle}>Close</button>
							{!templateSaved && <button className={`neu prim`}
								onClick={() => {
									this.handleSave();
								}}>Save</button>}
						</div>
					</div>
				</div>
                <ModalBody className="modalBody">
                    {/* Success notification */}
                    {templateSaved && (
                        <div style={{
                            position: 'absolute',
                            top: '15px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            zIndex: 1000,
                            background: '#28a745',
                            color: 'white',
                            padding: '12px 24px',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        }}>
                            <i className="las la-check-circle mr-10"></i>
                            Document template saved successfully!
                        </div>
                    )}

                    {loading && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255,255,255,0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 999
                        }}>
                            <div className="text-center">
                                <i className="las la-spinner la-spin" style={{ fontSize: '48px', color: '#007bff' }}></i>
                                <p className="mt-20 text-lg">Loading Document Editor...</p>
                                <p className="text-muted">This may take a few moments</p>
                            </div>
                        </div>
                    )}
                    
                    {error && (
                        <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'rgba(255,255,255,0.95)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 999
                        }}>
                            <div className="text-center" style={{ maxWidth: '400px' }}>
                                <i className="las la-exclamation-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                                <h3 className="mt-20">Error Loading Editor</h3>
                                <p className="mt-10">{error}</p>
                                <button className="neu prim mt-20" onClick={this.createEmbeddedTemplate}>
                                    <i className="las la-redo mr-5"></i>
                                    Retry
                                </button>
                            </div>
                        </div>
                    )}

                    {embedUrl && !loading && !error && (
                        <iframe
                            ref={this.iframeRef}
                            src={embedUrl}
                            title="Document Editor"
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                display: 'block'
                            }}
                            allow="fullscreen"
                        />
                    )}
                </ModalBody>
            </Modal>
        );
    }
}

export default BoldSignEditorModal;
